import { type JSONData } from "../../base_classes/dto.d";
import { Dto } from "../../base_classes/dto";
import { type IUserProfileDto, type IAccessLevel } from "./dto.gen.d";
import { Media } from "../media";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

// ENUMS
export enum Permission {
  NONE = "none",
  READ = "read",
  READ_WRITE = "read_write",
}
export const PermissionValues = Object.values(Permission);
export const toPermission = (s: string): Permission => {
  switch (s) {
    case "none":
      return Permission.NONE;
    case "read":
      return Permission.READ;
    case "read_write":
      return Permission.READ_WRITE;
    default:
      return Permission.NONE;
  }
};
export enum Language {
  EN = "en",
  NL = "nl",
}
export const LanguageValues = Object.values(Language);
export const toLanguage = (s: string): Language => {
  switch (s) {
    case "en":
      return Language.EN;
    case "nl":
      return Language.NL;
    default:
      return Language.EN;
  }
};

export class UserProfileDto extends Dto implements IUserProfileDto {
  /// ///////////////////////////////////////
  /// //////// PRIVATE VARIABLES ////////////
  /// ///////////////////////////////////////
  _path = "/user_profile/{id}";
  _reference = "/UserProfile/{id}";

  /// ///////////////////////////////////////
  /// ///////// PUBLIC VARIABLES ////////////
  /// ///////////////////////////////////////

  accessLevel: IAccessLevel = {
    admin: Permission.NONE,
    airlines: Permission.NONE,
    apiDocs: Permission.NONE,
    packingSlips: Permission.NONE,
    pricing: Permission.NONE,
    priceConfiguration: Permission.NONE,
    printingOrders: Permission.NONE,
    quotation: Permission.NONE,
    sampleTest: Permission.NONE,
    tools: Permission.NONE,
    userManagement: Permission.NONE,
  };

  address = {} satisfies Record<string, any>;

  avatar = new Media();

  avatarUrl = "";

  email = "";

  firstName = "";

  infix = "";

  isAdmin = false;

  _kind = "UserProfile";

  language = Language.NL;

  lastName = "";

  prefix = "";

  /// ///////////////////////////////////////
  /// ///////////// METHODS /////////////////
  /// ///////////////////////////////////////

  static FromJSON(data: unknown): UserProfileDto {
    // Create a new instance of the class
    const obj = new UserProfileDto();
    obj.set(data as JSONData);
    return obj;
  }

  static FromJSONArray(data: unknown[]): UserProfileDto[] {
    return data.map((item) => UserProfileDto.FromJSON(item));
  }

  override set(data: JSONData): void {
    super.set(data);

    // Implement variables here
    if (data.accessLevel !== undefined && data.accessLevel !== null) {
      if (data.accessLevel) {
        this.accessLevel = AccessLevel.FromJSON(data.accessLevel);
      }
    }
    if (data.address !== undefined && data.address !== null) {
      this.address = (data.address as Record<string, any>) ?? this.address;
    }
    if (data.avatar !== undefined && data.avatar !== null) {
      if (!this.avatar) {
        this.avatar = new Media();
      }
      this.avatar.set((data.avatar as JSONData) ?? this.avatar.toJSON());
    }
    if (data.avatarUrl !== undefined && data.avatarUrl !== null) {
      this.avatarUrl = (data.avatarUrl as string) ?? this.avatarUrl;
    }
    if (data.email !== undefined && data.email !== null) {
      this.email = (data.email as string) ?? this.email;
    }
    if (data.firstName !== undefined && data.firstName !== null) {
      this.firstName = (data.firstName as string) ?? this.firstName;
    }
    if (data.infix !== undefined && data.infix !== null) {
      this.infix = (data.infix as string) ?? this.infix;
    }
    if (data.isAdmin !== undefined && data.isAdmin !== null) {
      this.isAdmin = (data.isAdmin as boolean) ?? this.isAdmin;
    }
    if (data._kind !== undefined && data._kind !== null) {
      this._kind = (data._kind as string) ?? this._kind;
    }
    if (data.language !== undefined && data.language !== null) {
      this.language = toLanguage((data?.language as string) ?? this.language);
    }
    if (data.lastName !== undefined && data.lastName !== null) {
      this.lastName = (data.lastName as string) ?? this.lastName;
    }
    if (data.prefix !== undefined && data.prefix !== null) {
      this.prefix = (data.prefix as string) ?? this.prefix;
    }
  }
}

export class AccessLevel implements IAccessLevel {
  /// ///////////////////////////////////////
  /// ///////// PUBLIC VARIABLES ////////////
  /// ///////////////////////////////////////

  admin = Permission.NONE;

  airlines = Permission.NONE;

  apiDocs = Permission.NONE;

  packingSlips = Permission.NONE;

  pricing = Permission.NONE;

  priceConfiguration = Permission.NONE;

  printingOrders = Permission.NONE;

  quotation = Permission.NONE;

  sampleTest = Permission.NONE;

  tools = Permission.NONE;

  userManagement = Permission.NONE;

  /// ///////////////////////////////////////
  /// ///////////// METHODS /////////////////
  /// ///////////////////////////////////////

  static FromJSON(data: unknown): AccessLevel {
    // Create a new instance of the class
    const obj = new AccessLevel();
    obj.set(data as JSONData);
    return obj;
  }

  static FromJSONArray(data: unknown[]): AccessLevel[] {
    return data.map((item) => AccessLevel.FromJSON(item));
  }

  set(data: JSONData): void {
    // Implement variables here
    if (data.admin !== undefined && data.admin !== null) {
      this.admin = toPermission((data?.admin as string) ?? this.admin);
    }
    if (data.airlines !== undefined && data.airlines !== null) {
      this.airlines = toPermission((data?.airlines as string) ?? this.airlines);
    }
    if (data.apiDocs !== undefined && data.apiDocs !== null) {
      this.apiDocs = toPermission((data?.apiDocs as string) ?? this.apiDocs);
    }
    if (data.packingSlips !== undefined && data.packingSlips !== null) {
      this.packingSlips = toPermission(
        (data?.packingSlips as string) ?? this.packingSlips,
      );
    }
    if (data.pricing !== undefined && data.pricing !== null) {
      this.pricing = toPermission((data?.pricing as string) ?? this.pricing);
    }
    if (
      data.priceConfiguration !== undefined &&
      data.priceConfiguration !== null
    ) {
      this.priceConfiguration = toPermission(
        (data?.priceConfiguration as string) ?? this.priceConfiguration,
      );
    }
    if (data.printingOrders !== undefined && data.printingOrders !== null) {
      this.printingOrders = toPermission(
        (data?.printingOrders as string) ?? this.printingOrders,
      );
    }
    if (data.quotation !== undefined && data.quotation !== null) {
      this.quotation = toPermission(
        (data?.quotation as string) ?? this.quotation,
      );
    }
    if (data.sampleTest !== undefined && data.sampleTest !== null) {
      this.sampleTest = toPermission(
        (data?.sampleTest as string) ?? this.sampleTest,
      );
    }
    if (data.tools !== undefined && data.tools !== null) {
      this.tools = toPermission(
        (data?.tools as string) ?? this.tools,
      );
    }
    if (data.userManagement !== undefined && data.userManagement !== null) {
      this.userManagement = toPermission(
        (data?.userManagement as string) ?? this.userManagement,
      );
    }
  }
}
